import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import { signOut } from '../../services/auth';
import './sidebarDashboard.css';

function Sidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { landId, cropId } = useParams();
  const [user, setUser] = useState({ name: '', role: '' });

  const getRoleName = (role) => {
    const roles = {
      'owner': 'صاحب أرض',
      'partner': 'تابع لصاحب أرض',
      'engineer': 'مهندس زراعي',
      'manager': 'مسؤول عن أرض'
    };
    return roles[role] || '---';
  };

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) return;

      const { data: profile, error: dbError } = await supabase
        .from('users')
        .select('full_name, role')
        .eq('id', authUser.id)
        .single();

      if (dbError) {
        setUser({
          name: authUser.user_metadata?.full_name || '---',
          role: getRoleName(authUser.user_metadata?.role),
        });
        return;
      }

      if (profile) {
        setUser({
          name: profile.full_name,
          role: getRoleName(profile.role),
        });
      }
    };
    fetchUser();
  }, []);

  const handleLogoutClick = async () => {
    onClose();
    if (window.confirm("هل أنت متأكد أنك تريد تسجيل الخروج؟")) {
      const result = await signOut();
      if (result.success) navigate('/login', { replace: true });
    }
  };

  const baseRoute = `/dashboard/land/${landId}/crop/${cropId}`;

  const menuItems = [
    { name: 'الوضع المالي', icon: 'dashboard', path: baseRoute, end: true },
    { name: 'العوائد', path: `${baseRoute}/income`, icon: 'sell' },
    { name: 'المصاريف', path: `${baseRoute}/expenses`, icon: 'payments' },
    { name: 'العمالة', path: `${baseRoute}/labor`, icon: 'groups' },
    { name: 'الكيماويات', path: `${baseRoute}/chemicals`, icon: 'experiment' },
    { name: 'دورات الرش', path: `${baseRoute}/spray-cycles`, icon: 'event_available' },
    { name: 'الري', path: `${baseRoute}/irrigation`, icon: 'water_drop' },
    { name: 'الآلات الزراعية', path: `${baseRoute}/machines`, icon: 'agriculture' },
    { name: 'صيانة المعدات', path: `${baseRoute}/maintenance`, icon: 'build' },
    { name: 'الاقتراحات', path: `${baseRoute}/suggestions`, icon: 'tips_and_updates' },
    { name: 'دردشة AI', path: `${baseRoute}/ai-chat`, icon: 'smart_toy' },
    { name: 'التقارير', path: `${baseRoute}/reports`, icon: 'bar_chart' },
  ];

  return (
    <aside className={`diwan-sidebar-container ${isOpen ? 'is-active' : ''}`}>
      <div className="diwan-sidebar-header">
        <Link to="/" className="diwan-sidebar-logo-link">
          <img src="/img/logo.jpeg" alt="Logo" className="diwan-sidebar-logo-img" />
          <div className="diwan-sidebar-logo-info">
            <span className="diwan-sidebar-logo-name">ديوان الأرض</span>
            <span className="diwan-sidebar-logo-desc">إدارة مزارع ذكية</span>
          </div>
        </Link>
      </div>

      <div className="sidebar-quick-actions">
        <NavLink to={`/land/${landId}/select-crop`} className="quick-action-btn" onClick={onClose}>
          <span className="material-symbols-outlined">west</span>
          الزراعات
        </NavLink>
        <NavLink to="/select-land" className="quick-action-btn" onClick={onClose}>
          <span className="material-symbols-outlined">grid_view</span>
          الأراضي
        </NavLink>
      </div>

      <nav className="diwan-sidebar-nav-wrapper">
        <ul>
          {menuItems.map((item, index) => (
            <li key={index}>
              <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }) => isActive ? "diwan-sidebar-nav-link is-link-active" : "diwan-sidebar-nav-link"}
                onClick={onClose}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.name}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="diwan-sidebar-footer-area">
        <NavLink to={`${baseRoute}/settings`} className="diwan-sidebar-nav-link" onClick={onClose}>
          <span className="material-symbols-outlined">settings</span>
          <span>الإعدادات</span>
        </NavLink>

        <div
          className={`diwan-sidebar-profile-card ${location.pathname.includes('/account') ? 'is-card-active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => { navigate(`${baseRoute}/account`); onClose(); }}
        >
          <div className="diwan-sidebar-profile-info">
            <span className="diwan-sidebar-user-name">{user.name}</span>
            <span className="diwan-sidebar-user-role">{user.role}</span>
          </div>
          <div className="diwan-sidebar-profile-avatar">
            <img
              src="https://api.dicebear.com/7.x/avataaars/svg?seed=Jade"
              alt="User"
              className="topbar-avatar"
              style={{ borderRadius: "50%", width: "35px", height: "35px" }}
            />
            <span className="diwan-sidebar-icon-label">حسابي</span>
          </div>
        </div>

        {/* Mobile-only feedback and logout */}
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLSfHRdh26_WlS8-67VShpVUtKbvUtC3_sKluUdPc_tTQFsIVOA/viewform?usp=header"
          target="_blank"
          rel="noopener noreferrer"
          className="diwan-sidebar-nav-link hide-on-desktop"
          style={{ color: '#485935', marginTop: '10px' }}
          onClick={onClose}
        >
          <span className="material-symbols-outlined">rate_review</span>
          <span>شاركنا رأيك</span>
        </a>

        <div
          className="diwan-sidebar-nav-link hide-on-desktop"
          onClick={handleLogoutClick}
          style={{ cursor: 'pointer', color: '#dc3545', marginTop: '10px' }}
        >
          <span className="material-symbols-outlined" style={{ color: '#dc3545' }}>logout</span>
          <span>تسجيل خروج</span>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;